import React, {useCallback, useMemo, useRef} from 'react';
import styled from 'styled-components';
import {currentResource, labels} from '../../../../../../../../util/utils';
import {financialStatementType} from '../../../../../../../../util/uiconstants';
import AgGridWrapper from '../../../../../../BaseComponentWrappers/AgGridWrapper';
import ProgressBar from '../../../../../../../../components/Common/BaseComponentWrappers/MotifProgressBar';
import Fetch from '../../../../../../../Common/Fetch/fetch';
import NoRowsOverlay from './NoRowsOverlay';

export default function ClientDataTable({
	columnDefs,
	frameworkComponents,
	autoGroupColumnDef,
	getDataRow,
	trialBalanceData,
	optionsPerPage,
	page,
	pageSize,
	pageCount,
	onPageSizeChanged,
	onPageChanged
}) {
	const containerStyle = useMemo(() => ({width: '100%', height: '100%'}), []);
	const gridRef = useRef();

	const handleOnGridReady = useCallback((params) => {
		const grids = document.querySelectorAll(`section.AGGridWrapperSection`);
		if (grids && grids.length > 0) {
			grids.forEach((grid) => {
				const agBodyViewport = grid.querySelector('.ag-body-viewport');
				const agFullWidthRow = grid.querySelector('.ag-full-width-row');
				if (agBodyViewport) {
					agBodyViewport.classList.add('customScrollbar');
				}
				if (agFullWidthRow) {
					agFullWidthRow ?? agFullWidthRow.classList.add('customScrollbar');
				}
			});
			resizeGrid(params);

			window.addEventListener('resize', function() {
				resizeGrid(params);
			});
		}
	}, []);

	const resizeGrid = (params) => {
		setTimeout(function() {
			params.api.sizeColumnsToFit();
		}, 500);
	};

	const gridStyle = useMemo(() => ({height: '100%', width: '100%'}), []);

	const headerComponentParams = {
		template: `
					<div class="ag-cell-label-container" role="presentation">
						<span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
						<div ref="eLabel" class="ag-header-cell-label" role="presentation">
							<span ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>
							<span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>
							<span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>
							<span ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>
							<span ref="eText" class="ag-header-cell-text" role="columnheader" style="white-space: normal;"></span>
							<span ref="eFilter" class="ag-header-icon ag-filter-icon"></span>
						</div>
					</div>`
	};

	const defaultColDef = useMemo(() => {
		return {
			minWidth: 100,
			sortable: false,
			resizable: false,
			suppressMenu: true,
			wrapText: false,
			autoHeight: true,
			suppressMovable: true,
			headerComponentParams: headerComponentParams
		};
	}, []);

	const getDataPath = useMemo(() => {
		return (data) => {
			let arc = data?.arc;

			if (data.glAccountName) {
				//for nodes we add the id to make them unique
				arc.push(data.glAccountName + data.id);
			}
			return arc;
		};
	}, []);

	const getRowClass = (params) => {
		if (params.data && params.data.financialStatementSubTypeId === financialStatementType.AccountClass) {
			return 'row-annotation';
		}
	};

	const getRowHeight = (params) => {
		if (params.data && params.data.fullWidthRow) {
			// set here row height needed when annotation is there
			return 100;
		}
	};

	const noRowsOverlayComponent = useMemo(() => {
		return NoRowsOverlay;
	}, []);

	return (
		<StyledClientDataTable className="StyledClientDataTable">
			<section style={containerStyle} className="AgGridContainerStyles">
				<section style={gridStyle} className="AGGridWrapperSection">
					<Fetch fetch={getDataRow}>
						<AgGridWrapper
							getRowHeight={getRowHeight}
							getRowClass={getRowClass}
							rowData={trialBalanceData.length > 0 ? trialBalanceData : []}
							columnDefs={trialBalanceData.length > 0 ? columnDefs : []}
							autoGroupColumnDef={autoGroupColumnDef}
							defaultColDef={defaultColDef}
							animateRows
							groupDefaultExpanded={4}
							groupDisplayType={'singleColumn'}
							noRowsOverlayComponent={noRowsOverlayComponent}
							components={frameworkComponents}
							suppressRowClickSelection
							suppressHorizontalScroll
							suppressCellFocus
							wrapHeaderText
							autoHeaderHeight
							suppressContextMenu
							suppressGroupRowsSticky
							onReady={handleOnGridReady}
							loadingOverlayComponent={ProgressBar}
							loadingOverlayComponentParams={{
								isIndeterminate: true,
								circle: true
							}}
							suppressAggFuncInHeader
							domLayout="normal"
							getDataPath={getDataPath}
							treeData
							customPagination
							optionsPerPage={optionsPerPage}
							page={page}
							pageSize={pageSize}
							pageCount={pageCount}
							onPageSizeChanged={onPageSizeChanged}
							onPageChanged={onPageChanged}
							paginationButtonLabels={currentResource.pagingSvgHoverText}
							paginationSelectLabel={labels.pagingShowtext}
						/>
					</Fetch>
				</section>
			</section>
		</StyledClientDataTable>
	);
}

const StyledClientDataTable = styled.section`
	height: 100%;
	.AGGridWrapperSection {
		.ag-ltr {
			.ag-row-group-indent-0,
			.ag-row-group-indent-1,
			.ag-row-group-indent-2,
			.ag-row-group-indent-3 {
				align-items: center;
				.ag-group-expanded,
				.ag-group-contracted {
					min-height: var(--px-22);
					height: auto;
					margin-right: var(--px-5);
					.ag-icon {
						&::before {
							fill: var(--neutrals-900);
							color: var(--neutrals-900);
						}
					}
				}
			}
			.ag-row-group-indent-1 {
				padding-left: var(--px-20);
			}
			.ag-row-group-indent-2 {
				align-items: center;
				padding-left: var(--px-45);
			}
			.ag-row-group-indent-3 {
				align-items: center;
				padding-left: var(--px-70);
			}
			.ag-row-group-indent-4 {
				align-items: center;
				padding-left: var(--px-75);
			}
			.ag-row-group-leaf-indent {
				margin-left: var(--px-20);
				&.ag-row-group-indent-2 {
					padding-left: var(--px-48);
				}
				&.ag-row-group-indent-3 {
					padding-left: var(--px-50);
					.ag-group-value {
						width: 95%;
						.StyledNameColumn {
							font-size: var(--px-12) !important;
							width: 100%;
						}
					}
				}
				&.ag-row-group-indent-4 {
					.ag-group-value {
						width: 95%;
						.StyledNameColumn {
							width: 100%;
						}
					}
				}
			}
		}
		.ag-header {
			.ag-header-viewport {
				.ag-header-container {
					.ag-header-row {
						.ag-header-group-cell {
							font-size: var(--px-14);
							font-weight: 400;
							padding: 0 0 0 var(--px-10);
							justify-content: flex-end;
							&[col-id='7_0'] {
								justify-content: center;
								.StyledIconColumnHeader {
									justify-content: center;
								}
							}
						}
						.ag-header-cell {
							font-size: var(--px-14);
							font-weight: 400;
							&[col-id='ag-Grid-AutoColumn'] {
								padding-left: var(--px-24);
								.ag-header-cell-text,
								.ag-header-cell-comp-wrapper {
									font-size: var(--px-14) !important;
									font-weight: 400 !important;
									line-height: var(--px-24) !important;
									justify-content: flex-start !important;
									text-align: left;
									color: var(--neutrals-900);
								}
							}
							&[col-id='analysisPeriodBalance'] {
								padding-right: var(--px-10);
								padding-left: var(--px-10);
								.ag-header-cell-label,
								.ag-header-cell-comp-wrapper {
									justify-content: flex-end;
									text-align: right;
									color: var(--neutrals-900);
								}
							}
							&[col-id='analysisComparativeBalanceChange'] {
								padding-right: var(--px-10);
								padding-left: var(--px-10);
								.ag-header-cell-label,
								.ag-header-cell-comp-wrapper {
									justify-content: flex-end;
									text-align: right;
									color: var(--neutrals-900);
								}
							}
							&[col-id='comparativePeriodBalance'] {
								padding-right: var(--px-10);
								padding-left: var(--px-10);
								.ag-header-cell-label,
								.ag-header-cell-comp-wrapper {
									justify-content: flex-end;
									text-align: right;
									color: var(--neutrals-900);
								}
							}
							&[col-id='secondComparativePeriodBalance'] {
								padding-right: var(--px-10);
								padding-left: var(--px-10);
								.ag-header-cell-label,
								.ag-header-cell-comp-wrapper {
									justify-content: flex-end;
									text-align: right;
									color: var(--neutrals-900);
								}
							}
							&[col-id='analysisComparativeBalanceChangePercent'] {
								padding-right: var(--px-10);
								padding-left: var(--px-10);
								.ag-header-cell-label,
								.ag-header-cell-comp-wrapper {
									justify-content: flex-end;
									text-align: right;
									color: var(--neutrals-900);
								}
							}
							&[col-id='analysisSecondComparativeBalanceChange'] {
								padding-right: var(--px-10);
								padding-left: var(--px-10);
								.ag-header-cell-label .ag-header-cell-comp-wrapper {
									justify-content: flex-end;
									text-align: right;
									color: var(--neutrals-900);
								}
							}
							&[col-id='analysisSecondComparativeBalanceChangePercent'] {
								padding-right: var(--px-10);
								padding-left: var(--px-10);
								.ag-header-cell-label,
								.ag-header-cell-comp-wrapper {
									justify-content: flex-end;
									text-align: right;
									color: var(--neutrals-900);
								}
							}
							&[col-id='id'] {
								.ag-header-cell-label,
								.ag-header-cell-comp-wrapper {
									justify-content: center;
									text-align: center;
									color: var(--neutrals-900);
								}
							}
							&[col-id='analysisPeriodBalance_1'] {
								padding-right: var(--px-10);
								padding-left: var(--px-10);
								.ag-header-cell-label,
								.ag-header-cell-comp-wrapper {
									justify-content: center;
									text-align: center;
									color: var(--neutrals-900);
								}
							}
						}
					}
				}
			}
		}
		.ag-body-viewport {
			.ag-full-width-container {
				.ag-row {
					&.ag-row-level-3 {
						padding: 0 var(--px-110) !important;
						overflow-y: auto;
					}
					&.ag-full-width-row {
						overflow-y: hidden;
					}
				}
			}
			.ag-row {
				&.ag-row-level-0 {
					.ag-cell {
						&[col-id='ag-Grid-AutoColumn'] {
							.StyledNameColumn {
								font-size: var(--px-14);
								font-weight: 400;
								line-height: var(--px-24);
								color: var(--neutrals-900);
							}
						}
						&[col-id='id'] {
							.StyledIconColumn {
								cursor: default;
								svg {
									cursor: default;
								}
							}
						}
					}
				}
				&.ag-row-level-1 {
					.ag-cell {
						&[col-id='ag-Grid-AutoColumn'] {
							.StyledNameColumn {
								font-size: var(--px-14);
								font-weight: 400;
								line-height: var(--px-24);
								color: var(--neutrals-900);
							}
						}
						&[col-id='id'] {
							.StyledIconColumn {
								cursor: default;
								svg {
									cursor: default;
								}
							}
						}
					}
				}
				&.ag-row-level-2 {
					.ag-cell {
						&[col-id='ag-Grid-AutoColumn'] {
							.StyledNameColumn {
								font-size: var(--px-14);
								font-weight: 400;
								line-height: var(--px-24);
								color: var(--neutrals-900);
							}
						}
					}
				}
				&.ag-row-level-3 {
					padding: 0 0 0 var(--px-110) !important;
					.ag-cell {
						&[col-id='ag-Grid-AutoColumn'] {
							.StyledNameColumn {
								font-size: var(--px-14);
								font-weight: 400;
								line-height: var(--px-24);
								color: var(--neutrals-900);
							}
						}
					}
				}
				&.ag-row-level-4 {
					.ag-cell {
						&[col-id='ag-Grid-AutoColumn'] {
							.StyledNameColumn {
								font-size: var(--px-12);
								font-weight: 300;
								line-height: var(--px-16);
								color: var(--neutrals-900);
							}
						}
					}
				}
				.ag-row-selected {
					background-color: var(--neutrals-100) !important;
					&.ag-row-even,
					&.ag-row-odd {
						background-color: var(--neutrals-100) !important;
					}
				}
				.ag-cell {
					&[col-id='ag-Grid-AutoColumn'] {
						padding-left: var(--px-24);
						color: var(--neutrals-900);
					}
					&[col-id='analysisPeriodBalance'] {
						padding-right: var(--px-10);
						padding-left: var(--px-10);
						color: var(--neutrals-900);
						display: inline-flex;
						align-items: center;
						justify-content: flex-end;
						.ag-cell-value {
							display: flex;
							justify-content: flex-end;
						}
					}
					&[col-id='analysisComparativeBalanceChange'] {
						padding-right: var(--px-10);
						padding-left: var(--px-10);
						color: var(--neutrals-900);
						border-left: var(--px-2) solid var(--neutrals-200);
					}
					&[col-id='comparativePeriodBalance'] {
						padding-right: var(--px-10);
						padding-left: var(--px-10);
						color: var(--neutrals-900);
						display: inline-flex;
						align-items: center;
						justify-content: flex-end;
						.ag-cell-value {
							display: flex;
							justify-content: flex-end;
						}
					}
					&[col-id='secondComparativePeriodBalance'] {
						padding-right: var(--px-10);
						padding-left: var(--px-10);
						color: var(--neutrals-900);
						display: inline-flex;
						align-items: center;
						justify-content: flex-end;
						.ag-cell-value {
							display: flex;
							justify-content: flex-end;
						}
					}
					&[col-id='analysisComparativeBalanceChangePercent'] {
						padding-right: var(--px-10);
						padding-left: var(--px-10);
						color: var(--neutrals-900);
						display: inline-flex;
						align-items: center;
						justify-content: flex-end;
						.ag-cell-value {
							display: flex;
							justify-content: flex-end;
						}
					}
					&[col-id='analysisSecondComparativeBalanceChange'] {
						padding-right: var(--px-10);
						padding-left: var(--px-10);
						color: var(--neutrals-900);
					}
					&[col-id='analysisSecondComparativeBalanceChangePercent'] {
						padding-right: var(--px-10);
						padding-left: var(--px-10);
						color: var(--neutrals-900);
						display: inline-flex;
						align-items: center;
						justify-content: flex-end;
						.ag-cell-value {
							display: flex;
							justify-content: flex-end;
						}
					}
					&[col-id='analysisPeriodBalance_1'] {
						padding-right: var(--px-10);
						padding-left: var(--px-10);
						color: var(--neutrals-900);
						text-align: center;
						align-items: center;
						justify-content: center;
						display: inline-flex;
						.text-renderer {
							.ellipses {
								justify-content: center;
							}
						}
					}
					&[col-id='id'] {
						display: flex;
						align-items: center;
						justify-content: center;
						text-align: center;
						color: var(--neutrals-900);
						border-left: var(--px-2) solid var(--neutrals-200);
						svg {
							color: var(--neutrals-900);
						}
					}
				}
			}
		}
	}
	.helixLinkWrapper {
		display: flex;
		font-weight: 700;
		font-size: var(--px-14);
		margin: 0 var(--px-5) var(--px-16) 0;
		&.DisabledHelixLink {
			color: var(--neutrals-700);
		}
		.AnnotationsButton {
			cursor: pointer;
			margin-left: auto;
			color: var(--blue-600);
			&.DisabledExpandCollapse {
				color: var(--neutrals-700);
				pointer-events: none;
			}
			&:hover {
				text-decoration: underline;
			}
		}
		.helixSetURLEnabled {
			font-weight: 700;
			font-size: var(--px-14);
			color: var(--blue-600);
			&:hover,
			&:focus {
				border: 0 !important;
				outline: none !important;
				box-shadow: none;
			}
			&:hover {
				text-decoration: underline;
			}
		}
		.motif-tooltip-wrapper {
			.motif-tooltip-trigger-wrap {
				cursor: pointer;
			}
		}
	}
	.ag-root .ag-sticky-top {
		top: var(--px-40) !important;
	}
`;
